import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { API } from "../lib/api";
import { AdminNav } from "../components/AdminNav";
import CumulativeGrowthChart from "../components/CumulativeGrowthChart";
import { getAdminToken, adminLogout } from "../lib/adminAuth";
import { Loader2, Users, Repeat, BookOpen, Inbox, DollarSign, CalendarClock } from "lucide-react";

const usd = (c) => `$${((c || 0) / 100).toFixed(2)}`;

const relTime = (iso) => {
  if (!iso) return "";
  const d = (Date.now() - new Date(iso).getTime()) / 1000;
  if (d < 60) return "just now";
  if (d < 3600) return `${Math.floor(d / 60)}m ago`;
  if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
  return `${Math.floor(d / 86400)}d ago`;
};

const Kpi = ({ icon: Icon, label, value, sub, testId }) => (
  <div className="rounded-2xl bg-ivory-50 p-5 shadow-sm ring-1 ring-ivory-400/60" data-testid={testId}>
    <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-gold/10 text-gold-deep"><Icon className="h-4.5 w-4.5" /></span>
    <p className="mt-3 font-display text-3xl text-ink">{value}</p>
    <p className="text-sm text-ink-soft">{label}</p>
    {sub && <p className="mt-0.5 text-xs text-ink-muted">{sub}</p>}
  </div>
);

export default function AdminOverview() {
  const [token] = useState(() => getAdminToken());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (tok) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/overview`, { headers: { Authorization: `Bearer ${tok}` } });
      setData(data);
    } catch (e) {
      if (e.response?.status === 401) { adminLogout(); }
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (token) load(token); }, [token, load]);

  const logout = () => adminLogout();

  return (
    <div className="min-h-[100dvh]" style={{ background: "linear-gradient(180deg,#F3EFEA 0%,#ECE7DE 100%)" }} data-testid="admin-overview">
      <div className="mx-auto max-w-5xl px-5 py-10 sm:px-8">
        <AdminNav onLogout={logout} />
        <h1 className="font-display text-3xl text-ink">Overview</h1>
        <p className="mt-1 text-sm text-ink-soft">A calm view of your readers, revenue, and the quiet work coming in.</p>

        {loading || !data ? (
          <div className="mt-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-ink-muted" /></div>
        ) : (
          <>
            <CumulativeGrowthChart token={token} onAuthError={logout} />
            <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <Kpi icon={Repeat} label="Active Vault subscribers" value={data.vault_subscribers} testId="kpi-vault-subs" />
              <Kpi icon={DollarSign} label="Vault MRR" value={usd(data.vault_mrr_cents)} sub={`${data.immersion_active} active Immersion`} testId="kpi-mrr" />
              <Kpi icon={DollarSign} label="Total revenue" value={usd(data.revenue_cents)} sub={`${data.orders_count} orders`} testId="kpi-revenue" />
              <Kpi icon={BookOpen} label="Book owners" value={data.book_owners} testId="kpi-book-owners" />
              <Kpi icon={Inbox} label="Unread reflections" value={data.unread_reflections} testId="kpi-unread" />
              <Kpi icon={CalendarClock} label="Orientation clients" value={data.orientation_clients} sub={`${data.pending_immersion_apps} pending immersion`} testId="kpi-orientation" />
            </div>

            <div className="mt-10 rounded-2xl bg-ivory-50 p-6 shadow-sm ring-1 ring-ivory-400/60" data-testid="recent-activity">
              <p className="font-display text-lg text-ink">Recent activity</p>
              <div className="mt-3 divide-y divide-ivory-400/50">
                {(data.recent_activity || []).length === 0 && <p className="py-4 text-sm text-ink-muted">Nothing yet — it will fill as readers arrive.</p>}
                {(data.recent_activity || []).map((a, i) => (
                  <div key={i} className="flex items-center justify-between gap-4 py-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm text-ink">{a.label}</p>
                      <p className="truncate text-xs text-ink-muted">{a.email}</p>
                    </div>
                    <span className="flex-none text-xs text-ink-muted">{relTime(a.at)}</span>
                  </div>
                ))}
              </div>
            </div>
            <p className="mt-4 text-xs text-ink-muted">Revenue reflects Stripe test/sandbox mode until you go live.</p>
          </>
        )}
      </div>
    </div>
  );
}
